import {
  Button,
  Constraint,
  Container,
  FlexGrid,
  Header,
  Text,
} from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import { ANALYTICS_EVENTS } from '../../constants';
import styles from './SavingsTools.module.css';

const cx = classnames.bind(styles);

const TOOLS = [
  {
    name: 'Trip Cost Calculator',
    key: 'tripCostCalculator',
    pageName: 'Trip_Cost_Calculator',
    description: 'Plan your next road trip with our handy tool that can help you save.',
    href: '//www.gasbuddy.com/tripcostcalculator',
  },
  {
    name: 'Outage Tracker',
    key: 'outageTracker',
    pageName: 'Outage_Tracker',
    description: 'Find out which stations have power and fuel during natural disasters.',
    href: '//tracker.gasbuddy.com',
  },
  {
    name: 'Gas Price Map',
    key: 'gasPriceMap',
    pageName: 'Gas_Price_Map',
    description: 'Explore real-time gas prices in specific areas or wide-angle trends.',
    href: '//www.gasbuddy.com/gaspricemap',
  },
  {
    name: 'Fuel Insights',
    key: 'fuelInsights',
    pageName: 'Fuel_Insights',
    description: 'Explore price trends by various regions and time periods.',
    href: '//fuelinsights.gasbuddy.com',
  },
  {
    name: 'Fuel Logbook',
    key: 'fuelLogbook',
    pageName: 'Fuel_Logbook',
    description: 'Log your fuel purchases and track your fuel economy',
    href: '/account/fuel-log-book',
  },
  {
    name: 'Vehicle Recalls',
    key: 'vehicleRecalls',
    pageName: 'Vehicle_Recalls',
    description: 'Find out if your vehicle has any recalls, and what to do about it!',
    href: '/vehicles/recalls/search',
  },
];

export default function SavingsTools({
  className,
  tools,
  ...rest
}) {
  const analytics = useAnalytics();
  const { t } = useTranslation('marketing');

  const linkClickHandler = useCallback(pageName => () => {
    if (pageName === 'Vehicle_Recalls') {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.OPEN_RECALL_SEARCH_PAGE,
        attributes: {
          Page: pageName,
          source: 'saving_tools_link',
        },
      });
    } else {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.SAVINGS_LINK_CLICK,
        attributes: { Page: pageName },
      });
    }
  }, [analytics]);

  const toolClickHandlers = useMemo(
    () => tools.map(({ pageName }) => linkClickHandler(pageName)),
    [linkClickHandler, tools],
  );

  return (
    <div className={cx('container', className)} {...rest}>
      <Container>
        <Header as="h2" className={cx('header')}>
          {t('savingsTools.header', { ns: 'marketing' })}
        </Header>
        <Text as="p" centered className={cx('text')}>
          {t('savingsTools.description', { ns: 'marketing' })}
        </Text>
        <br />
        <br />
        <Constraint centered tablet={10} desktop={8}>
          <FlexGrid className={cx('tools')} container>
            {tools.map(({ href, key }, index) => (
              <FlexGrid.Column key={href} mobile={12} tablet={6}>
                <Button
                  as="a"
                  className={cx('tool')}
                  size="lg"
                  fluid
                  href={href}
                  onClick={toolClickHandlers[index]}
                  secondary
                >
                  <span className={cx('toolLinkLabel')}>
                    {t(`savingsTools.${key}_name`, { ns: 'marketing' })}
                  </span>
                  <p className={cx('toolLinkDescription')}>
                    {t(`savingsTools.${key}_description`, { ns: 'marketing' })}
                  </p>
                </Button>
              </FlexGrid.Column>
            ))}
          </FlexGrid>
        </Constraint>
      </Container>
    </div>
  );
}

SavingsTools.propTypes = {
  className: PropTypes.string,
  tools: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  })),
};

SavingsTools.defaultProps = {
  className: undefined,
  tools: TOOLS,
};
